import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react"

// --- State ---

export interface AssignmentFormState {
  customerId: string | null
  siteId: string | null
  systemId: string | null
  createNewSystem: boolean
  newSystemName: string
}

const initialState: AssignmentFormState = {
  customerId: null,
  siteId: null,
  systemId: null,
  createNewSystem: false,
  newSystemName: "",
}

// --- Actions ---

interface AssignmentFormActions {
  setCustomer: (customerId: string | null) => void
  setSite: (siteId: string | null) => void
  setSystem: (systemId: string | null) => void
  setCreateNewSystem: (checked: boolean) => void
  setNewSystemName: (name: string) => void
  reset: () => void
}

// --- Contexts (split to prevent unnecessary re-renders) ---

const AssignmentFormStateContext = createContext<AssignmentFormState | undefined>(undefined)
const AssignmentFormActionsContext = createContext<AssignmentFormActions | undefined>(undefined)

// --- Provider ---

export const AssignmentFormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AssignmentFormState>(initialState)

  const setCustomer = useCallback((customerId: string | null) => {
    setState({
      customerId,
      siteId: null,
      systemId: null,
      createNewSystem: false,
      newSystemName: "",
    })
  }, [])

  const setSite = useCallback((siteId: string | null) => {
    setState((prev) => ({
      ...prev,
      siteId,
      systemId: null,
      createNewSystem: false,
      newSystemName: "",
    }))
  }, [])

  const setSystem = useCallback((systemId: string | null) => {
    setState((prev) => ({
      ...prev,
      systemId,
    }))
  }, [])

  const setCreateNewSystem = useCallback((checked: boolean) => {
    setState((prev) => ({
      ...prev,
      createNewSystem: checked,
      systemId: checked ? null : prev.systemId,
      newSystemName: checked ? prev.newSystemName : "",
    }))
  }, [])

  const setNewSystemName = useCallback((name: string) => {
    setState((prev) => ({
      ...prev,
      newSystemName: name,
    }))
  }, [])

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  // Actions are stable (useCallback with no deps) so this object never changes
  const actions = useMemo<AssignmentFormActions>(
    () => ({ setCustomer, setSite, setSystem, setCreateNewSystem, setNewSystemName, reset }),
    [setCustomer, setSite, setSystem, setCreateNewSystem, setNewSystemName, reset],
  )

  return (
    <AssignmentFormStateContext.Provider value={state}>
      <AssignmentFormActionsContext.Provider value={actions}>
        {children}
      </AssignmentFormActionsContext.Provider>
    </AssignmentFormStateContext.Provider>
  )
}

// --- Hooks ---

export function useAssignmentFormState(): AssignmentFormState {
  const context = useContext(AssignmentFormStateContext)
  if (!context) throw new Error("useAssignmentFormState must be used within AssignmentFormProvider")
  return context
}

export function useAssignmentFormActions(): AssignmentFormActions {
  const context = useContext(AssignmentFormActionsContext)
  if (!context) throw new Error("useAssignmentFormActions must be used within AssignmentFormProvider")
  return context
}

/**
 * Hook that only subscribes to specific fields from state.
 * Components using this will only re-render when the selected fields change.
 */
export function useAssignmentFormField<K extends keyof AssignmentFormState>(field: K): AssignmentFormState[K] {
  const state = useAssignmentFormState()
  return state[field]
}
